import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Inbox, Eye, Edit3, Sparkles, Rocket, Trash2, MoreVertical,
  Loader2, RefreshCw, Calendar, Bot, Hand,
} from "lucide-react";
import TranslationBadge from "@/components/admin/TranslationBadge";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";
import { pickLocale } from "@/lib/localize";

/**
 * Founder Portal — Content Inbox
 *
 * Sits directly below the "Daily Command Center" on /admin/dashboard.
 * Shows the founder every article that needs their attention TODAY:
 *   • latest 10 imports (any source, any status)
 *   • live counters (new today, drafts, waiting NL, ready to publish, published today)
 *   • quick actions per row (Review · Edit · Translate · Publish/Unpublish · Delete)
 *
 * The component is intentionally lean:
 *   – reuses `adminApi` (no new fetch layer)
 *   – reuses `<TranslationBadge>` for EN/NL status pills
 *   – reuses existing publish/delete/translate endpoints (no new server logic)
 *
 * Future-ready: the row is a small pure sub-component — plugging in
 * scheduled-at, AI quality score, SEO score or a preview thumbnail later
 * is a one-line extension.
 */

function fmt(n) {
  if (n === undefined || n === null) return "—";
  return typeof n === "number" ? n.toLocaleString() : String(n);
}

function relTime(iso, t) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("admin.inbox.time.now");
  if (mins < 60) return t("admin.inbox.time.mins", { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("admin.inbox.time.hrs", { n: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return t("admin.inbox.time.days", { n: days });
  return new Date(iso).toLocaleDateString();
}

function CounterTile({ testId, label, value, hint, accent = "gold" }) {
  const accentColor = accent === "green"
    ? "text-emerald-400"
    : accent === "fog"
    ? "text-bone/50"
    : "text-gold";
  return (
    <div
      data-testid={testId}
      className="border border-white/10 bg-white/[0.02] px-4 py-3 flex flex-col justify-between min-h-[76px]"
    >
      <p className="text-[9px] tracking-[0.28em] uppercase text-bone/55 leading-tight">{label}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`font-serif text-2xl md:text-[26px] ${accentColor}`}>{value}</span>
        {hint && <span className="text-[10px] tracking-[0.22em] uppercase text-bone/40">{hint}</span>}
      </div>
    </div>
  );
}

function ActionButton({ onClick, disabled, title, testId, icon: Icon, tone = "default", href, target }) {
  const cls =
    tone === "danger"
      ? "text-bone/70 hover:text-red-400 hover:border-red-400/30"
      : tone === "green"
      ? "text-bone/70 hover:text-emerald-400 hover:border-emerald-400/30"
      : tone === "gold"
      ? "text-gold hover:bg-gold/10 border-gold/40"
      : "text-bone/70 hover:text-gold hover:border-gold/30";
  const base = `p-2 border border-transparent flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${cls}`;
  if (href) {
    return (
      <a href={href} target={target} rel={target === "_blank" ? "noreferrer" : undefined} data-testid={testId} title={title} className={base}>
        <Icon size={13} strokeWidth={1.5} />
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} title={title} className={base}>
      <Icon size={13} strokeWidth={1.5} />
    </button>
  );
}

function InboxRow({ doc, onAction, busy, t, lang }) {
  const title = pickLocale(doc.title, lang) || doc.slug;
  const published = doc.status === "published";
  const isAutoseo = doc.source === "autoseo";
  const [menuOpen, setMenuOpen] = useState(false);

  const editHref = `/admin/journal?edit=${doc.slug || doc.id}`;
  const publicHref = `/stoic-journal/${doc.slug}`;

  const actions = [
    {
      key: "review",
      icon: Eye,
      title: t("admin.inbox.action.review"),
      testId: `inbox-review-${doc.slug}`,
      href: published ? publicHref : editHref,
      target: published ? "_blank" : undefined,
    },
    {
      key: "edit",
      icon: Edit3,
      title: t("admin.inbox.action.edit"),
      testId: `inbox-edit-${doc.slug}`,
      href: editHref,
    },
    {
      key: "translate",
      icon: Sparkles,
      title: t("admin.inbox.action.translate"),
      testId: `inbox-translate-${doc.slug}`,
      tone: "gold",
      onClick: () => onAction("translate", doc),
    },
    {
      key: "publish",
      icon: Rocket,
      title: published ? t("admin.inbox.action.unpublish") : t("admin.inbox.action.publish"),
      testId: `inbox-publish-${doc.slug}`,
      tone: published ? "default" : "green",
      onClick: () => onAction("publish", doc),
    },
    {
      key: "delete",
      icon: Trash2,
      title: t("admin.inbox.action.delete"),
      testId: `inbox-delete-${doc.slug}`,
      tone: "danger",
      onClick: () => onAction("delete", doc),
    },
  ];

  return (
    <div className="py-3 border-b border-white/5 last:border-b-0" data-testid={`inbox-row-${doc.slug}`}>
      <div className="flex items-start gap-4">
        {/* Title + meta */}
        <div className="min-w-0 flex-1">
          <p className="font-serif text-[14.5px] md:text-[15px] text-bone leading-snug truncate">
            {title}
          </p>
          <div className="mt-1.5 flex items-center flex-wrap gap-x-2.5 gap-y-1.5">
            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase text-bone/50">
              <Calendar size={10} strokeWidth={1.5} />
              {relTime(doc.updated_at || doc.created_at, t)}
            </span>
            <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase ${isAutoseo ? "text-gold/80" : "text-bone/55"}`}>
              {isAutoseo ? <Bot size={10} strokeWidth={1.5} /> : <Hand size={10} strokeWidth={1.5} />}
              {isAutoseo ? t("admin.inbox.source.autoseo") : t("admin.inbox.source.manual")}
            </span>
            <span
              data-testid={`inbox-status-${doc.slug}`}
              className={`inline-flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase px-1.5 py-0.5 border ${
                published
                  ? "border-emerald-400/40 text-emerald-300/90 bg-emerald-400/5"
                  : "border-gold/40 text-gold bg-gold/5"
              }`}
            >
              {published ? t("admin.inbox.status.published") : t("admin.inbox.status.draft")}
            </span>
            <TranslationBadge article={doc} />
          </div>
        </div>

        {/* Desktop: inline actions */}
        <div className="hidden md:flex items-center gap-1 flex-shrink-0" data-testid={`inbox-actions-${doc.slug}`}>
          {actions.map((a) => (
            <ActionButton key={a.key} {...a} disabled={busy} />
          ))}
        </div>

        {/* Mobile: kebab menu */}
        <div className="md:hidden relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid={`inbox-menu-${doc.slug}`}
            className="p-2 text-bone/70 hover:text-gold border border-white/10"
            aria-label={t("admin.inbox.action.more")}
          >
            <MoreVertical size={15} strokeWidth={1.5} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 z-20 min-w-[180px] bg-ink border border-gold/40 shadow-xl">
                {actions.map((a) => {
                  const Icon = a.icon;
                  const inner = (
                    <span className="flex items-center gap-2 px-3 py-2.5 text-[11px] tracking-[0.22em] uppercase text-bone/85 hover:bg-white/[0.04] hover:text-gold">
                      <Icon size={12} strokeWidth={1.5} /> {a.title}
                    </span>
                  );
                  if (a.href) {
                    return (
                      <a
                        key={a.key}
                        href={a.href}
                        target={a.target}
                        rel={a.target === "_blank" ? "noreferrer" : undefined}
                        onClick={() => setMenuOpen(false)}
                        data-testid={`${a.testId}-mobile`}
                        className="block"
                      >
                        {inner}
                      </a>
                    );
                  }
                  return (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => { setMenuOpen(false); a.onClick(); }}
                      disabled={busy}
                      data-testid={`${a.testId}-mobile`}
                      className="block w-full text-left disabled:opacity-40"
                    >
                      {inner}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ContentInbox() {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [busyKind, setBusyKind] = useState(null); // 'translate'|'publish'|'delete'

  const load = async () => {
    setErr("");
    try {
      const d = await adminApi.inbox(10);
      setData(d);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handle = async (kind, doc) => {
    if (kind === "delete") {
      const title = pickLocale(doc.title, lang) || doc.slug;
      if (!window.confirm(t("admin.confirm.delete", { title }))) return;
    }
    setBusyId(doc.id);
    setBusyKind(kind);
    setErr("");
    try {
      if (kind === "translate") {
        await adminApi.translateArticle(doc.id);
      } else if (kind === "publish") {
        if (doc.status === "published") await adminApi.unpublishArticle(doc.id);
        else await adminApi.publishArticle(doc.id);
      } else if (kind === "delete") {
        await adminApi.deleteArticle(doc.id);
      }
      await load();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusyId(null);
      setBusyKind(null);
    }
  };

  const c = data?.counters || {};
  const articles = data?.articles || [];
  const empty = !loading && articles.length === 0;

  return (
    <section className="mb-14" data-testid="content-inbox">
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl md:text-3xl text-bone flex items-center gap-3" data-testid="inbox-title">
            <Inbox size={22} strokeWidth={1.5} className="text-gold" />
            {t("admin.inbox.title")}
          </h2>
          <p className="mt-1.5 text-sm text-bone/60 tracking-wide" data-testid="inbox-subtitle">
            {t("admin.inbox.subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setLoading(true); load(); }}
          disabled={loading}
          data-testid="inbox-refresh"
          className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-bone/70 border border-white/15 px-3 py-1.5 hover:text-gold hover:border-gold/40 disabled:opacity-40"
        >
          {loading ? <Loader2 size={12} strokeWidth={1.75} className="animate-spin" /> : <RefreshCw size={12} strokeWidth={1.75} />}
          {t("admin.inbox.refresh")}
        </button>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-6" data-testid="inbox-counters">
        <CounterTile testId="inbox-c-new-today"        label={t("admin.inbox.counter.newToday")}      value={fmt(c.new_today)} />
        <CounterTile testId="inbox-c-drafts"           label={t("admin.inbox.counter.drafts")}        value={fmt(c.drafts)} />
        <CounterTile testId="inbox-c-waiting-nl"       label={t("admin.inbox.counter.waitingNl")}     value={fmt(c.waiting_nl)}    accent={c.waiting_nl > 0 ? "gold" : "fog"} />
        <CounterTile testId="inbox-c-ready"            label={t("admin.inbox.counter.readyToPublish")} value={fmt(c.ready_to_publish)} accent="green" />
        <CounterTile testId="inbox-c-published-today"  label={t("admin.inbox.counter.publishedToday")} value={fmt(c.published_today)} accent="green" />
      </div>

      {/* List */}
      <div className="border border-gold/15 bg-white/[0.02] px-5 md:px-6" data-testid="inbox-list">
        {loading && (
          <p className="py-5 text-sm text-bone/50 flex items-center gap-2" data-testid="inbox-loading">
            <Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> {t("admin.label.loading")}
          </p>
        )}

        {!loading && empty && (
          <div className="py-10 text-center" data-testid="inbox-empty">
            <p className="font-serif text-xl text-bone">{t("admin.inbox.empty.title")}</p>
            <p className="mt-3 text-sm text-bone/60 italic max-w-md mx-auto leading-relaxed">
              {t("admin.inbox.empty.body")}
            </p>
          </div>
        )}

        {!loading && !empty && articles.map((doc) => (
          <InboxRow
            key={doc.id}
            doc={doc}
            onAction={handle}
            busy={busyId === doc.id}
            t={t}
            lang={lang}
          />
        ))}

        {busyId && busyKind === "translate" && (
          <p className="py-3 text-xs text-gold/80 border-t border-white/5" data-testid="inbox-translate-status">
            <Sparkles size={11} strokeWidth={1.75} className="inline mr-1" />
            {t("admin.journal.trans.ai.working")}
          </p>
        )}
      </div>

      {err && (
        <p className="mt-3 text-sm text-red-400" data-testid="inbox-error">{err}</p>
      )}
    </section>
  );
}
